import React, { useState, useEffect, useMemo } from 'react';
import { Accordion, Card, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap';
import Select from 'react-select';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { isEmpty } from 'lodash';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { SystemMasterSelect } from '../../Components/helper';
import Loader from '../../Components/Utility/Loader/Loader';
import BtnAdd from '../../Components/Utility/FilterAction/BtnAdd';
import * as action from './Store/SystemMasters.action';
import ConfirmationPopup from '../../Components/ModalPopup/ConfirmationPopup';
import { useComponent } from '../UserSetting/UsersLibrary/UserRights';
import { toasterMsg } from '../../Components/Toaster/Toaster';
import CustomTooltip from '../../Components/Tooltip/CustomTooltip';

const tempObj = {
  name: null,
  isEmpty: false,
  isNew: true,
  isActive: true,
  hasLength: false,
  _id: '',
};

function Category3Master() {
  const dispatch = useDispatch();
  const history = useHistory();
  const reducer = useSelector((estate) => estate);
  const {
    SystemMastersDetails,
    UserDetails,
    Users: { logInRights },
  } = reducer;
  const { category1List, category2List } = SystemMastersDetails;
  const { userInfo } = UserDetails;

  const { t } = useTranslation('common');
  const loggedInUserId = userInfo.userId;

  const [isLoadingCat3, setIsLoadingCat3] = useState(false);
  const [showCat3, setShowCat3] = useState(false);
  const [isCategory3Unique, setIsCategory3Unique] = useState(true);
  const [buttonDownCat3, setButtonDownCat3] = useState(true);
  const [cat3BtnDisable, setCat3BtnDisable] = useState(false);
  const [addingCat3, setAddingCat3] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showConfirmationAD, setShowConfirmationAD] = useState(false);
  const [isLoadingConf, setIsLoadingConf] = useState(false);
  const [isCat3, setIsCat3] = useState(false);
  const [cat3Data, setCat3Data] = useState([]);
  const [category3List, setCategory3List] = useState([]);
  const [isUniqCat3Id, setIsUniqCat3Id] = useState([]);
  const [deleteId, setDeleteId] = useState('');
  const [activeInactiveID, setActiveInactiveID] = useState('');
  const [scrollEl, setScrollEl] = useState();
  const [category3first, setCategory3first] = useState({});
  const [category3second, setCategory3second] = useState({});
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);

  const { CheckActionPermission } = useComponent();
  const [userRights, setUserRights] = useState({});
  useEffect(() => {
    if (logInRights) {
      setUserRights(logInRights);
    }
  }, [logInRights]);
  const MasterAddEditAccess = CheckActionPermission(userRights, 'masters', 'category', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(userRights, 'masters', 'category', 'delete');
  useEffect(() => {
    if (scrollEl) {
      scrollEl.scrollTop = 1000;
    }
  }, [scrollEl]);

  // Fetch CatBasedOnPT flag (drives whether Cat2 depends on Cat1)
  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      const catBasedOnPTRes = await dispatch(action.getCatBasedOnPT());
      setCatBasedOnPTData(catBasedOnPTRes);
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  useEffect(() => {
    setCat3Data(category3List);
    if (addingCat3) {
      setCat3Data([
        ...category3List,
        {
          name: null,
          isEmpty: false,
          isNew: true,
          isActive: true,
          hasLength: false,
          _id: '',
          aliasName: '',
        },
      ]);
    }
  }, [category3List, addingCat3]);

  const callApi = async () => {
    setButtonDownCat3(!buttonDownCat3);
    setIsLoadingCat3(true);
    if (buttonDownCat3) {
      setShowCat3(true);

      const cat3firstParams = {
        accessedFrom: 'masters',
        categoryType: 1,
        loggedInUserId,
      };
      const cat3seconParams = {
        accessedFrom: 'masters',
        categoryType: 2,
        loggedInUserId,
      };
      if (isEmpty(cat3Data)) {
        await dispatch(action.getCategory2Details(cat3seconParams));
        await dispatch(action.getCategory1Details(cat3firstParams));
      }
    } else {
      // Closing accordion - clear all state
      setShowCat3(false);
      setCategory3first({});
      setCategory3second({});
      setCategory3List([]);
      setCat3Data([]);
      setAddingCat3(false);
      setIsUniqCat3Id([]);
      setIsCategory3Unique(true);
      setCat3BtnDisable(false);
      setDeleteId('');
      setActiveInactiveID('');
      setShowConfirmation(false);
      setShowConfirmationAD(false);
      setIsCat3(false);
    }

    setIsLoadingCat3(false);
  };

  // Compute if selected Category 1 and 2 are active. If either is inactive, block Cat3 edits/ops.
  const areParentsActive = useMemo(() => {
    const c1Id = category3first?._id;
    const c2Id = category3second?._id;
    if (!c1Id || !c2Id) return true; // Allow when not fully selected
    const c1 = (category1List || []).find((x) => x?._id === c1Id);
    const c2 = (category2List || []).find((x) => x?._id === c2Id);
    if (!c1 || !c2) return true;
    return c1?.isActive === true && c2?.isActive === true;
  }, [category1List, category2List, category3first, category3second]);

  const handleChanged = (event, i) => {
    if (category3first?._id && category3second?._id && !areParentsActive) {
      toasterMsg('Cannot modify Category 3 items. Selected Category 1 or 2 is inactive. Please activate parent categories first.', 'danger');
      return;
    }
    const categories3 = [...cat3Data];
    if (event.target.name === 'aliasName') {
      categories3[i] = {
        ...categories3[i],
        aliasName: event.target.value,
      };
    } else {
      categories3[i] = {
        ...categories3[i],
        name: event.target.value,
        isEmpty: event.target.value === '',
      };
    }
    const arr = [];
    const arrIndex = [];
    categories3.forEach((ele, ind) => {
      categories3.forEach((objs, index) => {
        if (ele.name === objs.name && ind !== index) {
          arr.push(ele);
          arrIndex.push(index);
        }
      });
    });
    if (!isEmpty(arr)) {
      const arrs = [...arrIndex];
      const removeArss = Math.min(...arrs);
      const newArr = arrIndex.filter((ele) => ele !== removeArss);
      setIsUniqCat3Id([...newArr]);
    }
    setIsCategory3Unique(isEmpty(arr));
    let disableCat3 = false;
    categories3.forEach((category3Element) => {
      if (isEmpty(category3Element?.name?.trim())) {
        disableCat3 = true;
      }
    });
    setCat3BtnDisable(disableCat3);
    setCat3Data(categories3);
  };

  useEffect(() => {
    const array = [];
    cat3Data.forEach((ele, index) => {
      cat3Data.forEach((obj, places) => {
        if (index !== places && ele?.name === obj?.name) {
          array.push(ele);
        }
      });
    });
    if (isEmpty(array)) {
      setIsCategory3Unique(isEmpty(array));
      setIsUniqCat3Id([]);
    }
  }, [cat3Data]);

  const handleDelete = (id) => {
    if (category3first?._id && category3second?._id && !areParentsActive) {
      toasterMsg('Cannot delete Category 3 items. Selected Category 1 or 2 is inactive. Please activate parent categories first.', 'danger');
      return;
    }
    if (typeof id === 'number' || id === '') {
      cat3Data.splice(id, 1);
      cat3Data.forEach((category3Element) => {
        if (isEmpty(category3Element?.name?.trim()) && category3Element.isNew) {
          setCat3BtnDisable(true);
        } else {
          setCat3BtnDisable(false);
        }
      });
      setIsUniqCat3Id([]);
      setIsCategory3Unique(true);
      setCat3Data([...cat3Data]);
    } else {
      setDeleteId(id);
      setShowConfirmation(true);
    }
  };

  useEffect(() => {
    Object.keys(category3first).length !== 0 && Object.keys(category3second).length !== 0 && category3Called();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category3first, category3second]);

  const category3Called = async () => {
    const cat3params = {
      categoryOneId: category3first._id,
      categoryTwoId: category3second._id,
      categoryType: 3,
      loggedInUserId,
    };
    setCategory3List(await dispatch(action.getCategoryListForMasterCard(cat3params)));
  };

  const saveCat3 = async () => {
    setIsCat3(true);
    if (category3first?._id && category3second?._id && !areParentsActive) {
      toasterMsg('Cannot save Category 3 items. Selected Category 1 or 2 is inactive. Please activate parent categories first.', 'danger');
      setIsCat3(false);
      return;
    }
    if (!isEmpty(cat3Data)) {
      const emptyField = await cat3Data.filter((c3item) => c3item.name === null || c3item.name === '');

      if (isEmpty(emptyField)) {
        const data = cat3Data.map((ca3obj) => {
          return { ...ca3obj, name: ca3obj?.name?.trim(), aliasName: ca3obj?.aliasName?.trim() };
        });
        const cat3params = {
          loggedInUserId,
          categoriesName: JSON.stringify(data),
          categoryType: 3,
          title: t('CATEGORY3.CATEGORIESS'),
          categoryOneId: category3first._id,
          categoryTwoId: category3second._id,
        };
        setAddingCat3(false);
        await dispatch(action.saveToSystemMaster(cat3params, t));
        await category3Called();
        // post API
      } else {
        const newState = cat3Data.map((c3obj) => (c3obj?.isEmpty === false && c3obj.name === null ? { ...c3obj, isEmpty: true } : c3obj));
        setCat3Data(newState);
      }
    }
    setIsCat3(false);
  };

  const activeInactiveClicked = (id) => {
    if (category3first?._id && category3second?._id && !areParentsActive) {
      toasterMsg('Cannot change Category 3 status. Selected Category 1 or 2 is inactive. Please activate parent categories first.', 'danger');
      return;
    }
    setShowConfirmationAD(true);
    setActiveInactiveID(id);
  };

  const deleteConfirmed = async () => {
    setIsLoadingConf(true);
    const category3Params = {
      loggedInUserId,
      wantToDeleteCategoryId: deleteId,
      categoryType: 3,
      categoryOneId: category3first._id,
      categoryTwoId: category3second._id,
      title: t('CATEGORY3.CATEGORIES'),
    };
    await dispatch(action.deleteFromSystemMasters(category3Params, t));
    setIsLoadingConf(false);
    setShowConfirmation(false);
    setCat3Data([...category3List]);
    await category3Called();
  };

  const activeInactiveConfirmed = async () => {
    setIsLoadingConf(true);
    let c3 = {};
    cat3Data.forEach((ele) => {
      if (ele._id === activeInactiveID) {
        ele.isActive = !ele.isActive;
        const data = {
          ...ele,
          loggedInUserId,
          title: t('CATEGORY3.CATEGORIES'),
        };
        c3 = data;
      }
    });
    await dispatch(action.setActiveInactive(c3, t));
    setIsLoadingConf(false);
    setShowConfirmationAD(false);
    setCat3Data([...cat3Data]);
  };

  const addCat3 = async () => {
    if (category3first?._id && category3second?._id && !areParentsActive) {
      toasterMsg('Cannot add new Category 3 items. Selected Category 1 or 2 is inactive. Please activate parent categories first.', 'danger');
      return;
    }
    if (!showCat3) {
      setAddingCat3(true);
      setButtonDownCat3(!buttonDownCat3);
      setIsLoadingCat3(true);
      buttonDownCat3 ? setShowCat3(true) : setShowCat3(false);
      const cat3firstParams = {
        accessedFrom: 'masters',
        categoryType: 1,
        loggedInUserId,
      };
      const cat3seconParams = {
        accessedFrom: 'masters',
        categoryType: 2,
        loggedInUserId,
      };
      !showCat3 && (await dispatch(action.getCategory2Details(cat3seconParams))) && (await dispatch(action.getCategory1Details(cat3firstParams)));
      setIsLoadingCat3(false);
    } else if (!isEmpty(cat3Data)) {
      setCat3Data([...cat3Data, tempObj]);
    } else {
      setCat3Data([tempObj]);
    }
  };

  const checkCheckBox = (ele) => {
    if (!ele.hasLength) {
      ele.hasLength = true;
    } else {
      ele.hasLength = false;
    }
  };

  const handleCheckbox = (e) => {
    cat3Data.forEach((ele) => {
      if (ele._id === e.target.value) {
        checkCheckBox(ele);
      }
    });
    setCat3Data([...cat3Data]);
  };

  const btnCall = () => {
    if (buttonDownCat3 === false) {
      return <i className="icon-new-up" />;
    }
    return <i className="icon-new-down" />;
  };

  const renderTooltipOpenCloseCat3 = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      {!buttonDownCat3 ? t('CATEGORY3.TOOLTIP_CLOSE') : t('CATEGORY3.TOOLTIP_OPEN')}
    </Tooltip>
  );

  const renderTooltipActiveInactive = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      {t('CATEGORY3.ACTIVATE_INACTIVATE')}
    </Tooltip>
  );

  const renderTooltipDelete = (props) => (
    <Tooltip id="button-tooltip" {...props}>
      {t('CATEGORY3.DELETE')}
    </Tooltip>
  );

  const handleDropdownChange = (value, fromName) => {
    switch (value.type) {
      case 1:
        if (showCat3 && fromName === 'category3first') {
          setCategory3first(value);
          // When Cat2 depends on Cat1, reset Cat2 selection on Cat1 change
          if (catBasedOnPTData) {
            setCategory3second({});
          }
        }
        break;
      case 2:
        showCat3 && fromName === 'category3second' && setCategory3second(value);
        break;
      default:
        history.push({
          pathname: '/masters',
          state: null,
        });
        break;
    }
  };

  // Derive Category 2 options based on selected Category 1 when the flag is ON
  const filteredCategory2Options = useMemo(() => {
    if (!catBasedOnPTData) return category2List || [];
    const selectedCat1Id = category3first?._id;
    if (!selectedCat1Id) return [];
    return (category2List || []).filter((c2) => Array.isArray(c2?.catOneIds) && c2.catOneIds.includes(selectedCat1Id));
  }, [catBasedOnPTData, category2List, category3first]);

  const cat3Save = () => {
    return isCat3 ? (
      <Spinner animation="border" />
    ) : (
      <>
        <span className="icon-Save c-button-align f-20 mr-2" />
        {t('CATEGORY3.SAVE')}
      </>
    );
  };

  return (
    <>
      <Accordion className="mb-4 master-accordian">
        <Card>
          <Card.Header>
            <div className="title-container">
              <p>{t('CATEGORY3.TITLE')}</p>
            </div>
            <div className={!MasterAddEditAccess ? 'action-title-btns single-btn' : 'action-title-btns'}>
              <BtnAdd onBtnClick={addCat3} forDisable={cat3BtnDisable || !isCategory3Unique} addEditAccess={MasterAddEditAccess} />
              <OverlayTrigger placement={t('CATEGORY3.AUTOS')} delay={{ show: 250, hide: 400 }} overlay={renderTooltipOpenCloseCat3}>
                <button onClick={() => callApi()} type="button">
                  {btnCall()}
                </button>
              </OverlayTrigger>
            </div>
          </Card.Header>
          {showCat3 &&
            (isLoadingCat3 ? (
              <Loader />
            ) : (
              <Card.Body>
                <div className="select-container">
                  <Select
                    options={category1List}
                    placeholder={t('CATEGORY3.SELECT_CATEGORY1')}
                    getOptionLabel={(option) => `${option?.name}`}
                    getOptionValue={(option) => `${option?._id}`}
                    name="category3first"
                    value={Object.keys(category3first).length !== 0 && category3first}
                    styles={SystemMasterSelect}
                    onChange={(e) => handleDropdownChange(e, 'category3first')}
                  />
                  <Select
                    options={filteredCategory2Options}
                    placeholder={t('CATEGORY3.SELECT_CATEGORY2')}
                    getOptionLabel={(option) => `${option?.name}`}
                    getOptionValue={(option) => `${option?._id}`}
                    name="category3second"
                    isDisabled={!Object.keys(category3first).length}
                    value={Object.keys(category3second).length !== 0 && category3second}
                    styles={SystemMasterSelect}
                    onChange={(e) => handleDropdownChange(e, 'category3second')}
                  />
                </div>
                <PerfectScrollbar
                  containerRef={(ref) => {
                    setScrollEl(ref);
                  }}
                >
                  <ul>
                    {Object.keys(category3first).length !== 0 &&
                      Object.keys(category3second).length !== 0 &&
                      Array.isArray(cat3Data) &&
                      !isEmpty(cat3Data) &&
                      cat3Data?.map((object3, index3) => {
                        return (
                          <li key={index3}>
                            <input
                              name="aliasName"
                              value={cat3Data[index3]?.aliasName || ''}
                              onChange={(e) => handleChanged(e, index3)}
                              maxLength={5}
                              className="w-50px mr-2"
                              disabled={!MasterAddEditAccess || (category3first?._id && category3second?._id && !areParentsActive)}
                            />{' '}
                            <div className="input-container">
                              <div className="d-flex align-items-center">
                                <div className="checkbox-label">
                                  <input
                                    type="checkbox"
                                    name="category3CheckBox"
                                    id={`category3CheckBox_${index3}`}
                                    value={object3._id ? object3._id : ''}
                                    onChange={handleCheckbox}
                                    disabled={!MasterAddEditAccess || (category3first?._id && category3second?._id && !areParentsActive)}
                                    checked={object3.hasLength === true}
                                  />
                                  <label htmlFor={`category3CheckBox_${index3}`} className="chklabel">
                                    <span />
                                  </label>
                                </div>
                                <input
                                  key={index3}
                                  name="category3"
                                  autoFocus={!object3._id}
                                  value={object3.name ? object3.name : ''}
                                  placeholder={t('CATEGORY3.ENTER_CATEGORY3')}
                                  maxLength={30}
                                  disabled={!MasterAddEditAccess || (category3first?._id && category3second?._id && !areParentsActive)}
                                  type="text"
                                  onChange={(e) => handleChanged(e, index3)}
                                />
                              </div>
                              {!isEmpty(isUniqCat3Id) && isUniqCat3Id.includes(index3) && (
                                <label className="text-danger m-0 p-1">{t('CATEGORY3.ENTRY_ALREADY_EXIST')}</label>
                              )}

                              {object3.isEmpty && (object3.name === null || object3.name === '') && (
                                <label className="text-danger m-0 p-1">{t('CATEGORY3.REQUIRED_ENTRY')}</label>
                              )}
                              {object3.isNew && !isEmpty(object3.name) && isEmpty(object3?.name?.trim()) && (
                                <label className="text-danger m-0 p-1">{t('CATEGORY3.INVALID_ENTRY')}</label>
                              )}
                            </div>
                            <div className="input-action">
                              {object3._id !== '' && MasterAddEditAccess && (
                                <OverlayTrigger placement={t('CATEGORY3.AUTOS')} delay={{ show: 250, hide: 400 }} overlay={renderTooltipActiveInactive}>
                                  <button
                                    onClick={() => activeInactiveClicked(object3._id)}
                                    id={object3._id}
                                    type="button"
                                    aria-label={object3?.isActive ? 'active' : 'inactive'}
                                  >
                                    <span className={object3.isActive ? 'icon-active' : 'icon-inactive'} />
                                  </button>
                                </OverlayTrigger>
                              )}
                              {MasterDeleteAccess && (
                                <OverlayTrigger placement={t('CATEGORY3.AUTO')} delay={{ show: 250, hide: 400 }} overlay={renderTooltipDelete}>
                                  <button
                                    onClick={() => {
                                      handleDelete(object3._id ? object3._id : index3);
                                    }}
                                    type="button"
                                    aria-label="delete"
                                  >
                                    <span className="icon-delete mt-3 f-20" />
                                  </button>
                                </OverlayTrigger>
                              )}
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                </PerfectScrollbar>
                <div className="select-container">
                  <div className="instruction-container">
                    <span className="important-span">* </span>
                    {t('CATEGORY3.ADD_INSTRUCTIONS')}
                  </div>
                </div>
                <div className="section-save">
                  {MasterAddEditAccess && (
                    <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
                      <button
                        className="btn btn-page-filter btn-white"
                        onClick={saveCat3}
                        disabled={cat3BtnDisable || !isCategory3Unique || (category3first?._id && category3second?._id && !areParentsActive)}
                        type="button"
                        aria-label="Save"
                      >
                        {cat3Save()}
                      </button>
                    </CustomTooltip>
                  )}
                </div>
              </Card.Body>
            ))}
        </Card>
      </Accordion>
      {showConfirmation && (
        <ConfirmationPopup
          onOpen={showConfirmation}
          onConfirm={deleteConfirmed}
          title={t('CATEGORY3.DELETE_CONFIRM_TITLE')}
          message={t('CATEGORY3.DELETE_CONFIRM_MESSAGE')}
          toggle={() => {
            setShowConfirmation(!showConfirmation);
          }}
          loading={isLoadingConf}
        />
      )}
      {showConfirmationAD && (
        <ConfirmationPopup
          onOpen={showConfirmationAD}
          onConfirm={() => activeInactiveConfirmed()}
          title={t('CATEGORY3.ACTIVATE_DEACTIVATE_CONFIRM_TITLE')}
          message={t('CATEGORY3.ACTIVATE_DEACTIVATE_CONFIRM_MESSAGE')}
          toggle={() => {
            setShowConfirmationAD(!showConfirmationAD);
          }}
          loading={isLoadingConf}
        />
      )}
    </>
  );
}
export default Category3Master;
